// ─────────────────────────────────────────────────────────────────────────────
// printThermalReceipt
//
// Props received from SaleTable (new contract):
//   header        — { serialNo, partyName, date, time }
//   receiveRows   — derived rows array (each has: name, weight, amount,
//                   isPaid, isCharge, chargeSign, isSerialized)
//   givenRows     — same shape
//   recvTotals    — { weight, totalCash, amountDelta, totalPaid }
//   givnTotals    — same shape
//   cashBalance   — recvTotals.totalCash − givnTotals.totalCash
//   amountBalance — recvTotals.amountDelta + givnTotals.amountDelta  (signed)
//   netBalance    — cashBalance + amountBalance
//                     > 0  →  admin owes customer  ("payable")
//                     < 0  →  customer owes admin  ("receivable")
// ─────────────────────────────────────────────────────────────────────────────

export function printThermalReceipt({
  header,
  receiveRows,
  givenRows,
  recvTotals,
  givnTotals,
  cashBalance,
  amountBalance,
  netBalance,
}) {
  const serialNo = header.serialNo || "";
  const partyName = header.partyName || "";
  const date = header.date || "";
  const time = header.time || "";

  // ── Section display totals (plain cash + all charge amounts, unsigned) ──
  const sectionDisplayTotal = (rows, totals) => {
    const chargesSum = rows.reduce(
      (s, r) => s + (r.isCharge ? r.amount : 0),
      0,
    );
    return totals.totalCash + chargesSum;
  };

  const recvDisplayTotal = sectionDisplayTotal(receiveRows, recvTotals);
  const givnDisplayTotal = sectionDisplayTotal(givenRows, givnTotals);

  // ── Balance label ───────────────────────────────────────────────────────
  const balWeightVal = recvTotals.weight - givnTotals.weight;
  const balanceNote =
    netBalance > 0
      ? "Admin owes customer"
      : netBalance < 0
        ? "Customer owes admin"
        : "Settled \u2713";

  // ── Build rows HTML ─────────────────────────────────────────────────────
  let rowsHTML = "";

  const renderSection = (rows, label, totals, displayTotal) => {
    // section header
    rowsHTML += `
    <tr class="thermal-section-header">
      <td colspan="4">${label}</td>
    </tr>`;

    // data rows — serial counter only for isSerialized rows
    let serial = 0;
    rows.forEach((r) => {
      if (r.isSerialized) serial += 1;

      if (r.isPaid) {
        // paid sub-row — green, no serial, no weight
        rowsHTML += `
        <tr class="thermal-paid-row">
          <td></td>
          <td>${r.name}</td>
          <td></td>
          <td>${r.amount > 0 ? r.amount.toFixed(2) : ""}</td>
        </tr>`;
      } else if (r.isCharge) {
        // charge row — colour depends on chargeSign
        //   chargeSign = -1  → customer owes admin   → amber/debit class
        //   chargeSign = +1  → admin owes customer   → blue/credit class
        const cls =
          r.chargeSign === -1
            ? "thermal-charge-debit-row"
            : "thermal-charge-credit-row";
        rowsHTML += `
        <tr class="${cls}">
          <td>${serial}</td>
          <td>${r.name}</td>
          <td>${r.weight > 0 ? r.weight.toFixed(3) : ""}</td>
          <td>${r.amount > 0 ? r.amount.toFixed(2) : ""}</td>
        </tr>`;
      } else {
        // plain row
        rowsHTML += `
        <tr>
          <td>${r.isSubItem ? "" : serial}</td>
          <td>${r.name}</td>
          <td>${r.weight > 0 ? r.weight.toFixed(3) : ""}</td>
          <td>${r.amount > 0 ? r.amount.toFixed(2) : ""}</td>
        </tr>`;
      }
    });

    // section total row
    rowsHTML += `
    <tr class="thermal-total-row">
      <td>Total</td>
      <td></td>
      <td>${totals.weight > 0 ? totals.weight.toFixed(3) : ""}</td>
      <td>${displayTotal > 0 ? displayTotal.toFixed(2) : ""}</td>
    </tr>`;
  };

  renderSection(receiveRows, "Receive", recvTotals, recvDisplayTotal);
  renderSection(givenRows, "Given", givnTotals, givnDisplayTotal);

  // ── Balance row ─────────────────────────────────────────────────────────
  const wDisplay =
    balWeightVal !== 0
      ? `${balWeightVal > 0 ? "+" : ""}${balWeightVal.toFixed(3)}`
      : "\u2713";

  const cDisplay =
    netBalance !== 0
      ? `${netBalance > 0 ? "+" : ""}${netBalance.toFixed(2)}`
      : "\u2713";

  const balClass =
    netBalance > 0
      ? "thermal-balance-positive"
      : netBalance < 0
        ? "thermal-balance-negative"
        : "thermal-balance-settled";

  rowsHTML += `
  <tr class="thermal-balance-row ${balClass}">
    <td>Balance</td>
    <td style="font-size:8px;font-style:italic"></td>
    <td>${wDisplay}</td>
    <td>${cDisplay}</td>
  </tr>`;

  // ── Charges breakdown block ─────────────────────────────────────────────
  // Collect all charge rows from both sections for a summary
  const allChargeRows = [
    ...receiveRows.filter((r) => r.isCharge),
    ...givenRows.filter((r) => r.isCharge),
  ];

  // Separate by direction
  const customerOwesRows = allChargeRows.filter((r) => r.chargeSign === -1); // customer → admin
  const adminOwesRows = allChargeRows.filter((r) => r.chargeSign === 1); // admin → customer

  const totalCustomerOwes = customerOwesRows.reduce((s, r) => s + r.amount, 0);
  const totalAdminOwes = adminOwesRows.reduce((s, r) => s + r.amount, 0);

  // Paid amounts (both sections combined)
  const totalPaid = [...receiveRows, ...givenRows]
    .filter((r) => r.isPaid)
    .reduce((s, r) => s + r.amount, 0);

  // Net charge balance: customer's obligations minus what they've paid
  // (adminOwes charges cancel out customer debt)
  const netCharges = totalCustomerOwes - totalAdminOwes - totalPaid;

  // if (allChargeRows.length > 0) {
  //   rowsHTML += `
  //   <tr class="thermal-charges-header">
  //     <td colspan="4">Charges Summary</td>
  //   </tr>`;

  //   if (totalCustomerOwes > 0) {
  //     rowsHTML += `
  //     <tr class="thermal-charges-debit-row">
  //       <td colspan="3">Customer owes admin (Lab/Ginni + Given charges)</td>
  //       <td>${totalCustomerOwes.toFixed(2)}</td>
  //     </tr>`;
  //   }

  //   if (totalAdminOwes > 0) {
  //     rowsHTML += `
  //     <tr class="thermal-charges-credit-row">
  //       <td colspan="3">Admin owes customer (Receive charges)</td>
  //       <td>${totalAdminOwes.toFixed(2)}</td>
  //     </tr>`;
  //   }

  //   if (totalPaid > 0) {
  //     rowsHTML += `
  //     <tr class="thermal-charges-paid-row">
  //       <td colspan="3">Paid by customer</td>
  //       <td>-${totalPaid.toFixed(2)}</td>
  //     </tr>`;
  //   }

  //   const settledClass =
  //     netCharges <= 0
  //       ? "thermal-charges-settled-row"
  //       : "thermal-charges-due-row";
  //   const settledLabel =
  //     netCharges <= 0 ? "All charges settled \u2713" : "Charges still due";
  //   const settledVal = netCharges <= 0 ? "\u2713" : `+${netCharges.toFixed(2)}`;

  //   rowsHTML += `
  //   <tr class="${settledClass}">
  //     <td colspan="3">${settledLabel}</td>
  //     <td>${settledVal}</td>
  //   </tr>`;
  // }

  // ── Full receipt HTML ────────────────────────────────────────────────────
  const thermalHTML = `
  <div id="thermal-print-content">
    <div class="thermal-header">
      <h2>KW GOLD &amp; SILVER LAB</h2>
      <p>Sale Point Receipt</p>
      <p>Serial: ${serialNo} | Date: ${date} | Time: ${time}</p>
      <p>Party: ${partyName}</p>
    </div>

    <table class="thermal-table">
      <thead>
        <tr>
          <th>S.No</th>
          <th>Particular</th>
          <th>Weight</th>
          <th>Cash</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHTML}
      </tbody>
    </table>

    <div style="text-align:center;margin-top:20px;font-size:10px">
      <p>Thank you for your business!</p>
      <p>KW Gold &amp; Silver Lab</p>
    </div>
  </div>`;

  // ── Open print window ────────────────────────────────────────────────────
  const printWindow = window.open("", "_blank", "width=400,height=600");

  printWindow.document.write(`
  <html>
  <head>
  <title>Thermal Print</title>
  <style>

  body {
    font-family: "Courier New", monospace;
    font-size: 12px;
    width: 80mm;
    padding: 10px;
  }

  .thermal-header {
    text-align: center;
    border-bottom: 1px dashed #000;
    margin-bottom: 10px;
  }
  .thermal-header h2 { font-size: 16px; }

  .thermal-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
  }
  .thermal-table th,
  .thermal-table td {
    border: 1px solid #000;
    padding: 3px;
    text-align: center;
  }

  /* ── Section / structural rows ── */
  .thermal-section-header td { background: #ddd; font-weight: bold; }
  .thermal-total-row          { background: #f5f5f5; font-weight: bold; }

  /* ── Balance row ── */
  .thermal-balance-row        { font-weight: bold; }
  .thermal-balance-positive td { background: #e8f0ff;  } /* admin owes customer */
  .thermal-balance-negative td { background: #fff0f0;  } /* customer owes admin */
  .thermal-balance-settled td  { background: #f0fdf4; }

  /* ── Charge rows ── */
  /* customer owes admin (Lab/Ginni always, Given non-lab/ginni) */
  .thermal-charge-debit-row td  { font-weight: bold; }
  /* admin owes customer (Receive non-lab/ginni) */
  .thermal-charge-credit-row td {  font-weight: bold; }

  /* ── Paid sub-row ── */
  .thermal-paid-row td { background: #f0fdf4; }

  /* ── Charges summary block ── */
  .thermal-charges-header td {
    background: #e8e8e8;
    font-weight: bold;
    font-size: 9px;
    letter-spacing: 0.5px;
    border-top: 2px dashed #000;
  }
  .thermal-charges-debit-row td  { background: #fff4e0;  font-weight: bold; }
  .thermal-charges-credit-row td { background: #e8f0ff;  font-weight: bold; }
  .thermal-charges-paid-row td   { background: #f0fdf4;  }
  .thermal-charges-due-row td    { background: #fff1f1;  font-weight: bold; }
  .thermal-charges-settled-row td { background: #f0fdf4;  font-weight: bold; }

  @media print { body { width: 80mm; margin: 0; } }

  </style>
  </head>
  <body>

  ${thermalHTML}

  <script>
    window.onload = function () {
      window.print();
      window.onafterprint = () => window.close();
    };
  </script>

  </body>
  </html>
  `);

  printWindow.document.close();
}
